import { AppleBook, AppleBooksResult } from "./types";
import { appleBooksCache } from "./cache";
import { transformAppleBookData } from "./transformer";
import { supabase } from "@/integrations/supabase/client";
import { pickBestBookMatch } from "@/utils/bookIdentity";

export const searchAppleBooksByISBN = async (isbn: string): Promise<AppleBook | null> => {
  const cacheKey = appleBooksCache.generateKey(isbn);
  const cached = appleBooksCache.get(cacheKey);
  if (cached !== undefined) return cached; // Return cached result (can be null)
  
  try {
    console.log('📖 Searching Apple Books by ISBN via proxy:', isbn);
    
    const { data, error } = await supabase.functions.invoke('apple-books-proxy', {
      body: {
        searchType: 'isbn',
        isbn: isbn
      }
    });

    if (error) {
      console.error('Apple Books proxy error:', error);
      return null;
    }

    if (data?.error) {
      console.warn('Apple Books API error:', data.error);
      // Cache the "no result" to avoid repeated API calls
      appleBooksCache.set(cacheKey, null);
      return null;
    }
    
    if (!data?.results || data.results.length === 0) {
      // Cache the "no result" to avoid repeated API calls
      appleBooksCache.set(cacheKey, null);
      return null;
    }
    
    const book = transformAppleBookData(data.results[0]);
    if (book) {
      book.isbn = isbn; // Store the ISBN for reference
    }
    
    appleBooksCache.set(cacheKey, book);
    return book;
    
  } catch (error) {
    console.error('Error searching Apple Books by ISBN:', error);
    return null;
  }
};

export const searchAppleBooksByTitleAuthor = async (title: string, author: string): Promise<AppleBook | null> => {
  const cacheKey = appleBooksCache.generateKey(undefined, title, author);
  const cached = appleBooksCache.get(cacheKey);
  if (cached !== undefined) return cached; // Return cached result (can be null)
  
  try {
    console.log('🔍 Searching Apple Books by title/author via proxy:', { title, author });
    
    const { data, error } = await supabase.functions.invoke('apple-books-proxy', {
      body: {
        searchType: 'titleAuthor',
        title: title,
        author: author
      }
    });

    if (error) {
      console.error('Apple Books proxy error:', error);
      return null;
    }

    if (data?.error) {
      console.warn('Apple Books API error:', data.error);
      // Cache the "no result" to avoid repeated API calls
      appleBooksCache.set(cacheKey, null);
      return null;
    }
    
    if (!data?.results || data.results.length === 0) {
      // Cache the "no result" to avoid repeated API calls
      appleBooksCache.set(cacheKey, null);
      return null;
    }
    
    /**
     * Choose the right result, instead of trusting the ranking.
     *
     * This took data.results[0] unconditionally, and "strictly verify" below
     * only confirmed that the id it had already chosen still existed — it never
     * asked whether that was the book. Searching Apple for "Vulcan's Hammer" by
     * Philip K. Dick returns, in order: a narrated "Philip K. Dick's Vulcan's
     * Hammer - Unabridged" credited to "Philip K. Dick & Kevin Theis"; a
     * collection; and Theodore Sturgeon's "Slow Sculpture". All three come back
     * as kind='ebook', so nothing in the response separates them — the third
     * would have been accepted just as readily had it ranked first.
     *
     * Nothing good enough is a real answer: returning null lets the caller fall
     * back to Google Books, which beats confidently showing the wrong book with
     * a Buy button beside it.
     */
    const best = pickBestBookMatch(
      (data.results as AppleBooksResult[]).map((r) => ({
        raw: r,
        title: r.trackName,
        author: r.artistName,
      })),
      title,
      author,
    );

    if (!best) {
      console.info(`Apple Books: no result confidently matched "${title}" by ${author}.`);
      appleBooksCache.set(cacheKey, null);
      return null;
    }

    const candidate = best.raw;
    const { data: verifyData, error: verifyError } = await supabase.functions.invoke('apple-books-proxy', {
      body: {
        searchType: 'lookupId',
        id: candidate.trackId,
        title,
        author
      }
    });

    if (verifyError || verifyData?.error) {
      console.warn('Apple Books verification failed:', verifyError || verifyData?.error);
      appleBooksCache.set(cacheKey, null);
      return null;
    }

    if (!verifyData?.results || verifyData.results.length === 0) {
      appleBooksCache.set(cacheKey, null);
      return null;
    }

    const book = transformAppleBookData(verifyData.results[0]);
    appleBooksCache.set(cacheKey, book);
    return book;
    
  } catch (error) {
    console.error('Error searching Apple Books by title/author:', error);
    return null;
  }
};

// Main function to search Apple Books - tries ISBN first, then title/author
export const searchAppleBooks = async (
  title: string, 
  author: string, 
  isbn?: string
): Promise<AppleBook | null> => {
  console.log('🍎 Apple Books search started:', { title, author, isbn });
  
  // Try ISBN lookup first if available (more accurate and faster)
  if (isbn) {
    console.log('📖 Trying ISBN lookup first:', isbn);
    const result = await searchAppleBooksByISBN(isbn);
    if (result) {
      console.log('✅ ISBN lookup success:', result);
      return result;
    }
    console.log('❌ ISBN lookup failed, trying title/author');
  }
  
  // Fallback to title/author search
  console.log('🔍 Trying title/author search:', title, author);
  const result = await searchAppleBooksByTitleAuthor(title, author);
  console.log('📱 Title/author search result:', result);
  return result;
};